import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductShowcase = ({ products = [], loading = false }) => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  
  // Filter produk yang ditandai sebagai Best Seller
  // Jika tidak ada yang ditandai, ambil 3 produk pertama sebagai fallback
  const bestSellers = products.filter(p => p.is_best_seller).slice(0, 3);
  const displayProducts = bestSellers.length > 0 ? bestSellers : products.slice(0, 3);

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'premium':
        return <Badge variant="static" className="bg-amber-500 text-white border-none shadow-md">Premium</Badge>;
      case 'kering':
        return <Badge variant="static" className="bg-blue-600 text-white border-none shadow-md">Kering</Badge>;
      case 'basah':
      default:
        return <Badge variant="static" className="bg-green-600 text-white border-none shadow-md">Basah</Badge>;
    }
  };

  return (
    <section id="products" className="py-20 bg-white relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#FED09D]/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#B22222]/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animate-in fade-in slide-in-from-bottom duration-700">
          <Badge className="mb-4 bg-[#B22222] text-white px-4 py-1.5 text-sm uppercase tracking-widest font-bold border-none shadow-lg">
            Most Popular
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#3E2723] mb-4">
            Our Best Seller
          </h2>
          <p className="text-lg text-[#3E2723]/70 max-w-2xl mx-auto">
            {language === 'id' 
              ? 'Varian bakpia terfavorit yang paling banyak dicari dan dinikmati oleh pelanggan setia kami.' 
              : 'Our most popular bakpia variants, highly sought after and enjoyed by our loyal customers.'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <p className="text-[#3E2723]/70">{t.products.loading}</p>
            </div>
          ) : displayProducts.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-[#3E2723]/70">{t.products.noProducts}</p>
            </div>
          ) : (
            displayProducts.map((product, index) => {
              const productName = language === 'id' ? product.name : product.name_en || product.name;
              const productDesc = language === 'id' ? product.description : product.description_en || product.description;
              const imageUrl = product.image_url ? `${BACKEND_URL}${product.image_url}` : product.image;

              return (
                <Card
                  key={product.id}
                  className="group overflow-hidden border-2 border-transparent hover:border-[#B22222]/20 transition-all duration-500 hover:shadow-2xl animate-in fade-in slide-in-from-bottom cursor-pointer"
                  style={{ animationDelay: `${(index % 4) * 50}ms` }}
                  onClick={() => navigate('/order')}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={imageUrl}
                      alt={productName}
                      width="400"
                      height="256"
                      className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-8">
                       <span className="text-white font-bold flex items-center gap-2">
                         {t.products.seeDetail} <ArrowRight size={16} />
                       </span>
                    </div>
                    
                    <div className="absolute top-4 right-4 bg-[#B22222] text-white px-4 py-2 rounded-full font-bold shadow-lg">
                      Rp {product.price.toLocaleString()}
                    </div>

                    <div className="absolute top-4 left-4">
                      {getCategoryBadge(product.category)}
                    </div>
                  </div>

                  <CardContent className="p-6 bg-white">
                    <h3 className="text-xl font-bold text-[#3E2723] mb-3 group-hover:text-[#B22222] transition-colors duration-300 leading-tight">
                      {productName}
                    </h3>
                    <p className="text-[#3E2723]/70 text-sm mb-4 line-clamp-2">
                      {productDesc}
                    </p>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Action Button to Order Page */}
        {!loading && products.length > 0 && (
          <div className="text-center mt-16">
            <Button
              onClick={() => navigate('/order')}
              className="bg-transparent border-2 border-[#B22222] text-[#B22222] hover:bg-[#B22222] hover:text-white rounded-full px-10 py-7 h-auto text-xl font-bold transition-all duration-300 group shadow-lg hover:shadow-xl"
            >
              {t.products.seeAllMenu}
              <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform duration-300" size={24} />
            </Button>
            <p className="mt-4 text-[#3E2723]/50 font-medium italic">
              {t.products.availableVariants}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductShowcase;
